import React, { useState, useEffect } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faTimes } from "@fortawesome/free-solid-svg-icons";
import { useDispatch, useSelector } from "react-redux";
import { fetchLikedItems, removeFromFavourites } from "../../Providers/favouritesProvider";
import { useNavigate } from "react-router-dom";
import icons from "../../icons.config";
import { setClickedItem } from "../../store/itemsSlice";
import Lottie from "lottie-react";
import loadingAnimation from "../../Assets/loadingAnimation.json";
import { IMAGE_URL } from "../../api-urls";

const LikedItemsSlider = ({ toggleSlider }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  // Fetch authentication status from Redux (adjust based on your store structure)
  const isAuthenticated = useSelector((state) => state.auth?.isAuthenticated || false);

  // Fetch liked items when the component mounts, but only if authenticated
  useEffect(() => {
    if (isAuthenticated) {
      dispatch(fetchLikedItems());
    }
  }, [dispatch, isAuthenticated]);

  // Get liked items from Redux state
  const likedItems = useSelector((state) => state.favourites.likedItems || []);
  const loading = useSelector((state) => state.favourites.loading);
  const error = useSelector((state) => state.favourites.error);

  console.log("Fetched Liked Items:", likedItems); // Debugging API data

  const [buttonTexts, setButtonTexts] = useState({});

  // Remove item from favourites
  const handleRemove = (variantId) => {
    console.log("🛑 Removing item with variantId:", variantId);
    if (!variantId) {
      console.error("❌ Invalid variantId, cannot remove!");
      return;
    }
    dispatch(removeFromFavourites(variantId));
  };

  const goToShop = () => {
    navigate("/shop");
  };

  const handleViewProduct = (item) => {
    console.log("Item received in handleItemClick:", item);
    dispatch(setClickedItem(item));
    const itemUrl = item.url;
    navigate(`/${itemUrl}`);
  };

  const goToLogin = () => {
    navigate("/login"); // Adjust the route as per your app
  };

  return (
    <div className="fixed top-0 right-0 w-[80%] md:w-3/5 xl:w-2/5 h-full bg-tertiary shadow-lg z-50 transition-transform duration-300 ease-in-out overflow-y-scroll">
      {/* Close Button */}
      <div className="p-4 flex justify-end">
        <FontAwesomeIcon
          icon={faTimes}
          className="cursor-pointer text-primary1 text-xl"
          onClick={toggleSlider}
        />
      </div>

      <div className="px-7">
        {!isAuthenticated ? (
          // Beautiful "Please Log In" UI for unauthorized users
          <div className="flex flex-col items-center justify-center h-[80%] space-y-6">
            <img
              src={icons.noItemLiked} // Reuse your no-liked-items icon or use a custom lock/heart icon
              alt="Login required"
              className="w-24 h-24 opacity-80"
            />
            <h2 className="text-xl font-outfit text-hcolor font-medium text-center">
              Please Log In
            </h2>
            <p className="text-center text-gray-600 font-outfit text-sm max-w-xs">
              Sign in to view your favorite items and personalize your shopping experience!
            </p>
            <button
              onClick={goToLogin}
              className="border-2 py-2 px-6 text-tertiary font-outfit font-medium bg-hcolor hover:bg-tertiary hover:text-hcolor transition-all duration-300 rounded-md"
            >
              Log In
            </button>
            <p className="text-xs text-gray-500">
              Don’t have an account?{" "}
              <span
                onClick={() => navigate("/signup")}
                className="text-hcolor cursor-pointer hover:underline"
              >
                Sign Up
              </span>
            </p>
          </div>
        ) : loading ? (
          // Loading state
          <div className="flex justify-center items-center h-[400px]">
            <Lottie
              animationData={loadingAnimation}
              loop={true}
              className="w-[200px] h-[200px]"
            />
          </div>
        ) : error ? (
          <p className="text-center text-red-500">{error}</p>
        ) : likedItems.length > 0 ? (
          <>
            {/* Favourites Heading */}
            <div className="flex items-center mb-4">
              <h2 className="text-lg font-outfit text-hcolor">
                Favourites ({likedItems.length})
              </h2>
            </div>

            {/* Liked Items List */}
            {likedItems.map((item) => (
              <div
                key={item.id}
                className="relative flex gap-4 mb-4 items-stretch"
              >
                {/* Image Section */}
                <div className="relative w-[30%] md:w-[17%] lg:w-[22%] lg:h-[150px] min-h-[80px]">
                  <img
                    src={IMAGE_URL + item.variant_main_image}
                    alt={item.title}
                    className="w-full h-full object-cover"
                  />
                  <button
                    className="absolute -top-2 -right-2"
                    onClick={() => handleRemove(item?.variant_id)}
                  >
                    <img src={icons.removeLikedItem} alt="remove item" />
                  </button>
                </div>

                {/* Product Details */}
                <div className="flex flex-col flex-1">
                  <p className="font-outfit font-light text-base lg:text-2xl">
                    {item.product_name || "Unknown Product"}
                  </p>
                  <p className="font-outfit text-xs lg:text-sm font-normal text-hcolor">
                    Material: {item.dress_material || "Unknown"}
                  </p>
                  <p className="text-xs lg:text-sm text-gray-500">
                    Size: {item.selectedSize || "M/10"}
                  </p>

                  {/* View Button */}
                  <button
                    className={`py-2 border text-hcolor bg-tertiary md:mt-6 w-full hover:bg-hcolor hover:text-tertiary hover:border-tertiary text-sm 
                      ${
                        buttonTexts[item.id] && buttonTexts[item.id] !== "VIEW"
                          ? "lg:w-[50%] px-1"
                          : "lg:w-[47%]"
                      }`}
                    onClick={() => handleViewProduct(item)}
                  >
                    {buttonTexts[item.id] || "VIEW"}
                  </button>
                </div>

                {/* Pricing and Stock */}
                <div className="text-right">
                  <p className="text-lg font-bold text-primary">
                    Rs.{item.discount_price || "N/A"}
                  </p>
                  <p className="text-sm text-gray-500 line-through">
                    Rs.{item.price || "N/A"}
                  </p>
                </div>
              </div>
            ))}
          </>
        ) : (
          // Empty liked items state
          <div className="flex flex-col items-center mt-40 space-y-4">
            <img
              src={icons.noItemLiked}
              alt="No liked items"
              className="w-20"
            />
            <p className="text-center text-hcolor">No liked items yet!</p>
            <button
              onClick={goToShop}
              className="border-2 py-2 px-5 text-hcolor font-outfit font-medium hover:bg-hcolor hover:text-primary2 bg-tertiary transition-all duration-800"
            >
              Explore
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default LikedItemsSlider;

// import React, { useState, useMemo, useCallback } from "react";
// import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import { faTimes } from "@fortawesome/free-solid-svg-icons";
// import { useDispatch, useSelector } from "react-redux";
// import { removeLikedItem, clearAll } from "../../store/likedItemsSlice";
// import { useNavigate } from "react-router-dom";
// import icons from "../../icons.config";
// import { selectClickedItem } from "../../Providers/favouritesProvider";
// import { addToCart } from "../../Providers/cartProvider";
// import { createSelector } from "@reduxjs/toolkit";

// const selectFavouriteItems = createSelector(
//   (state) => state.favourites.items,
//   (items) => items
// );

// const selectProducts = (state) => state.products?.items || [];

// const LikedItemsSlider = ({ toggleSlider }) => {
//   const dispatch = useDispatch();
//   const navigate = useNavigate();

//   const likedItems = useSelector(selectFavouriteItems);
//   const products = useSelector(selectProducts);
//   const clickedItem = useSelector(selectClickedItem);

//   const [quantity, setQuantity] = useState(1);
//   const [buttonTexts, setButtonTexts] = useState({});

//   const handleRemove = useCallback(
//     (itemId) => dispatch(removeLikedItem({ id: itemId })),
//     [dispatch]
//   );

//   const handleClearAll = useCallback(() => dispatch(clearAll()), [dispatch]);

//   const goToShop = useCallback(() => navigate("/shop"), [navigate]);

//   const handleAddToCart = useCallback(
//     (item) => {
//       const selectedSize = item.selectedSize || "M/10";
//       if (item && item.discountedPrice) {
//         dispatch(addToCart({ ...item, quantity, selectedSize }));
//       } else {
//         console.error("Item is missing price or is undefined!");
//       }
//       setButtonTexts((prev) => ({ ...prev, [item.id]: "ADDED TO CART!" }));
//       setTimeout(() => {
//         setButtonTexts((prev) => ({ ...prev, [item.id]: "ADD TO CART" }));
//       }, 3000);
//     },
//     [dispatch, quantity]
//   );

//   const renderedLikedItems = useMemo(
//     () =>
//       likedItems.map((item) => {
//         const product = products.find((p) => p.id === item.id);
//         const price = product ? product.discountedPrice : "N/A";

//         return (
//           <div key={item.id} className="relative flex gap-4 mb-4">
//             <div className="relative w-[30%] md:w-[17%] lg:w-[20%]">
//               <img src={item.thumbnail} alt={item.title} className="w-full object-cover" />
//               <button
//                 className="absolute -top-2 -right-2"
//                 onClick={() => handleRemove(item.id)}
//               >
//                 <img src={icons.removeLikedItem} alt="remove item" />
//               </button>
//             </div>
//             <div className="flex flex-col flex-1">
//               <p className="font-outfit font-light text-base lg:text-2xl">{item.title}</p>
//               <p className="font-outfit text-xs lg:text-sm font-normal text-hcolor">Material: {item.material || "Unknown"}</p>
//               <p className="text-xs lg:text-sm text-gray-500">Size: {item.selectedSize || "M/10"}</p>
//               <button
//                 className={`py-2 border text-hcolor bg-tertiary md:mt-6 w-full text-sm ${
//                   buttonTexts[item.id] && buttonTexts[item.id] !== "ADD TO CART"
//                     ? "lg:w-[50%] px-1 whitespace-nowrap"
//                     : "lg:w-[47%]"
//                 }`}
//                 onClick={() => handleAddToCart(item)}
//               >
//                 {buttonTexts[item.id] || "ADD TO CART"}
//               </button>
//             </div>
//             <div className="text-right">
//               <p className="text-lg font-bold text-primary">Rs.{item.discountedPrice}</p>
//               <p className="text-sm text-gray-500 line-through">Rs.{item.price}</p>
//               <p className={`text-sm ${item.inStock ? "text-green-500" : "text-red-500"}`}>
//                 {item.inStock ? "In Stock" : "Out of Stock"}
//               </p>
//             </div>
//           </div>
//         );
//       }),
//     [likedItems, products, handleRemove, handleAddToCart, buttonTexts]
//   );

//   return (
//     <div className="fixed top-0 left-0 lg:-left-0 w-[90%] lg:w-1/3 h-full bg-tertiary shadow-lg z-50 transition-transform duration-300 ease-in-out overflow-y-scroll">
//       <div className="p-4 flex justify-end items-end">
//         <FontAwesomeIcon
//           icon={faTimes}
//           className="cursor-pointer text-primary1 text-xl"
//           onClick={toggleSlider}
//         />
//       </div>
//       <div className="px-7">
//         {likedItems.length > 0 ? (
//           <>
//             <div className="flex items-center mb-4">
//               <h2 className="text-lg font-outfit text-hcolor">
//                 Favourites ({likedItems.length})
//               </h2>
//               <button
//                 className="text-sm text-red-500 font-medium underline ml-auto"
//                 onClick={handleClearAll}
//               >
//                 Clear All
//               </button>
//             </div>
//             {renderedLikedItems}
//           </>
//         ) : (
//           <div className="flex flex-col items-center mt-40 space-y-4">
//             <img src={icons.noItemLiked} alt="No liked items" className="w-20" />
//             <p className="text-center text-hcolor">No liked items yet!</p>
//             <button
//               onClick={goToShop}
//               className="border-2 py-2 px-5 text-hcolor font-outfit font-medium hover:bg-hcolor hover:text-primary2 bg-tertiary transition-all duration-800"
//             >
//               Explore
//             </button>
//           </div>
//         )}
//       </div>
//     </div>
//   );
// };

// export default LikedItemsSlider;
